'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Box } from '@virginmediao2/momentum-nextjs/box';
import { Button } from '@virginmediao2/momentum-nextjs/button';
import {
  Card,
  CardFooter,
  CardHeader,
} from '@virginmediao2/momentum-nextjs/card';
import { Divider } from '@virginmediao2/momentum-nextjs/divider';
import { Flex } from '@virginmediao2/momentum-nextjs/flex';
import {
  ErrorAlert,
  ErrorAlertsRef,
  FormInput,
  FormRadioGroup,
  FormRadioInput,
  FormSelect,
  FormTextarea,
} from '@virginmediao2/momentum-nextjs/forms';
import { Icon, IconProps } from '@virginmediao2/momentum-nextjs/icon';
import { Text } from '@virginmediao2/momentum-nextjs/text';
import { StoryblokComponentProps } from '@virginmediao2/storyblok-nextjs';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  FalBriefcase,
  FalCalendarDays,
  FalCreditCard,
} from '@virginmediao2/momentum-icons/icons/fal';

import { sendComplaintsFormRequest } from '@/lib/complaints-form';
import { mobileNumberRegex } from '@/lib/utils/regex-validators';

import styles from './complaints-form.module.css';
import { complaintsList, complaintsListMap } from './complaintsList';

const SUBMISSION_SUCCESS_URL =
  'https://www.o2.co.uk/how-to-complain/complaints-form/thank-you';

const complaintsFormSchema = z
  .object({
    isCustomer: z.string({
      required_error: 'Please specify if you are a customer',
      invalid_type_error: 'Please specify if you are a customer',
    }),
    mobileNumber: z
      .string()
      .max(11)
      .regex(mobileNumberRegex, 'Invalid mobile number'),
    complaintRadio: z.string({
      required_error: 'Select one of the options above',
      invalid_type_error: 'Select one of the options above',
    }),
    issueDescription: z.string().min(1, 'Select an issue description'),
    issueType: z.string(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    addressLine1: z.string().min(1, 'Address line 1 is required'),
    addressLine2: z.string().optional(),
    town: z.string().min(1, 'Town is required'),
    country: z.string().min(1, 'Country is required'),
    postCode: z.string().min(1, 'Postcode is required'),
    issueDetails: z.string().min(50, 'The details requires at least 10 words'),
    howToFixTheIssue: z.string().min(1, 'How to fix the issue is required'),
    contactMobileNumber: z
      .string()
      .max(11)
      .regex(mobileNumberRegex, 'Invalid mobile number'),
    email: z
      .string()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: 'Invalid email address',
      }),
    confirmEmail: z
      .string()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: 'Invalid email address',
      }),
    preferContact: z.string(),
  })
  .superRefine(({ email, confirmEmail }, ctx) => {
    if (email && confirmEmail && email !== confirmEmail) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmEmail'],
        message: 'Email addresses do not match',
      });
    }
  });

enum optionNames {
  payMonthly = 'payMonthly',
  business = 'business',
  payAsYouGo = 'payAsYouGo',
}

const formOptionInputList: Array<{
  name: optionNames;
  label: string;
  icon: IconProps['icon'];
}> = [
  {
    name: optionNames.payMonthly,
    label: 'Pay Monthly',
    icon: FalCalendarDays,
  },
  {
    name: optionNames.business,
    label: 'Business',
    icon: FalBriefcase,
  },
  {
    name: optionNames.payAsYouGo,
    label: 'Pay as You Go',
    icon: FalCreditCard,
  },
];

const Options = ({
  optionsData,
}: {
  optionsData: Record<string, string>[];
}) => (
  <>
    {optionsData.map(({ label, value }) => (
      <option key={label} value={value}>
        {label}
      </option>
    ))}
  </>
);

type ComplaintsFormData = z.infer<typeof complaintsFormSchema>;
type ComplaintsFormProps = {
  errorMessage: JSX.Element;
  description: JSX.Element;
  title: JSX.Element;
};
export const ComplaintsForm = ({
  errorMessage,
  description,
  title,
}: ComplaintsFormProps & StoryblokComponentProps) => {
  const errorAlertsRef = useRef<ErrorAlertsRef>(null);

  const [apiError, setApiError] = useState(false);
  const [isCustomer, setIsCustomer] = useState(true);
  const [service, setService] = useState('');
  const [issueTypeOptions, setIssueTypeOptions] = useState<
    keyof typeof complaintsListMap | ''
  >('');

  const router = useRouter();
  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isValid },
  } = useForm<ComplaintsFormData>({
    resolver: zodResolver(complaintsFormSchema),
    mode: 'onSubmit',
  });

  const onSubmit = async (data: ComplaintsFormData) => {
    try {
      setApiError(false);
      if (!isValid) return;
      await sendComplaintsFormRequest({ ...data, service });

      reset();
      router.push(SUBMISSION_SUCCESS_URL);
    } catch (error) {
      setApiError(true);
      console.error('Submission error:', error);
    }
  };

  return (
    <Flex direction='column' className={styles['complaints-form']}>
      {apiError && (
        <Box margin='lg 0' border={false}>
          {errorMessage}
        </Box>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        {!isValid && errors && (
          <Box margin='lg 0' border={false}>
            <ErrorAlert
              ref={errorAlertsRef}
              errors={errors}
              setFocus={(name: string) => setFocus(name as any)}
            />
          </Box>
        )}

        <Box>
          {title}
          {description}
          <Box
            direction='row'
            directionSm='column'
            justify='between'
            margin='md 0'
          >
            <FormSelect
              id='isCustomer'
              description='Are you a customer?'
              {...register('isCustomer')}
              error={errors?.isCustomer}
              aria-placeholder='Select'
              onChange={(e) => {
                setIsCustomer(e.target.value === 'yes');
                setService('');
              }}
              marginBottom
            >
              <Options
                optionsData={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </FormSelect>

            {isCustomer && (
              <FormInput
                id='mobileNumber'
                description='Please enter your O2 mobile number'
                {...register('mobileNumber')}
                error={errors?.mobileNumber}
                placeholder='07917112233'
                maxLength={11}
              />
            )}
          </Box>

          <Text size='T400' weight='bold'>
            Your complaint
          </Text>
          <FormRadioGroup
            id='complaintRadio'
            description='Which of the following services does your complaint relate to?'
            error={errors?.complaintRadio?.message}
          >
            <Box
              direction='row'
              directionSm='column'
              justify='center'
              align='center'
              margin={'0 0 lg 0'}
            >
              {formOptionInputList.map((item) => (
                <Card
                  bg={isCustomer ? 'primary' : 'secondary'}
                  key={item.name}
                  border
                  padding={'lg'}
                  margin={'sm'}
                  rounded={'sm'}
                  align='center'
                  width={'full-width'}
                >
                  <CardHeader align='center'>
                    <Icon icon={item.icon} color='dark' size='lg' />
                  </CardHeader>
                  <CardFooter>
                    <FormRadioInput
                      label={item.label}
                      isFlag={false}
                      isBordered={false}
                      disabled={!isCustomer}
                      value='no'
                      {...register('complaintRadio')}
                      onChange={() => setService(item.name)}
                      checked={service === item.name}
                    />
                  </CardFooter>
                </Card>
              ))}
            </Box>
          </FormRadioGroup>
          <FormSelect
            id='issueDescription'
            label={'What issue best describes your problem?'}
            {...register('issueDescription')}
            error={errors?.issueDescription}
            aria-placeholder='Select'
            marginBottom
            onChange={(e) => {
              setIssueTypeOptions(
                e.target.value as keyof typeof complaintsListMap
              );
            }}
          >
            <Options optionsData={complaintsList} />
          </FormSelect>
          <FormSelect
            id='issueType'
            label={'What type of problem do you have?'}
            {...register('issueType')}
            error={errors?.issueType}
            aria-placeholder='Select'
            marginBottom
          >
            <Options
              optionsData={
                issueTypeOptions ? complaintsListMap[issueTypeOptions] : []
              }
            />
          </FormSelect>
        </Box>
        <Divider color='light' margin={'md 0 lg 0'} />
        <Box margin={'lg 0 lg'}>
          <Text weight='bold' size='T500' marginBottom>
            Your details
          </Text>
          <FormInput
            id='firstName'
            label={'First name'}
            {...register('firstName')}
            error={errors?.firstName}
            marginBottom
          />

          <FormInput
            id='lastName'
            label={'Last name'}
            {...register('lastName')}
            error={errors?.lastName}
            marginBottom
          />

          <Box
            direction='row'
            directionSm='column'
            justify='between'
            margin={'lg 0 lg'}
          >
            <FormInput
              id='addressLine1'
              label={'Address line 1'}
              {...register('addressLine1')}
              error={errors?.addressLine1}
              width='full-width'
            />
            <FormInput
              id='addressLine2'
              label={'Address line 2'}
              {...register('addressLine2')}
              error={errors?.addressLine2}
              width='full-width'
            />
          </Box>

          <Box
            direction='row'
            directionSm='column'
            justify='between'
            margin={'lg 0 lg'}
          >
            <FormInput
              id='town'
              label={'Town'}
              {...register('town')}
              error={errors?.town}
            />
            <FormInput
              id='country'
              label={'Country'}
              {...register('country')}
              error={errors?.country}
            />
          </Box>

          <FormInput
            id='postCode'
            label={'Post Code'}
            {...register('postCode')}
            error={errors?.postCode}
          />
        </Box>

        <FormTextarea
          id='issueDetails'
          dataTestId='issueDetails'
          label={<Text>Please provide details of your issue</Text>}
          {...register('issueDetails')}
          placeholder='Type here'
          error={errors?.issueDetails}
          marginBottom
        />

        <FormTextarea
          id='howToFixTheIssue'
          label={<Text>What would you like O2 to do about your issue?</Text>}
          dataTestId='howToFixTheIssue'
          {...register('howToFixTheIssue')}
          placeholder='Type here'
          error={errors?.howToFixTheIssue}
          marginBottom
        />

        <FormInput
          id='contactMobileNumber'
          label={'Contact telephone number'}
          {...register('contactMobileNumber')}
          error={errors?.contactMobileNumber}
          marginBottom
          maxLength={11}
        />
        <Box direction='row' directionSm='column' justify='between'>
          <FormInput
            id='email'
            label={'Email address'}
            {...register('email')}
            error={errors?.email}
            marginBottom
          />

          <FormInput
            id='confirmEmail'
            label={'Confirm email address'}
            {...register('confirmEmail')}
            error={errors?.confirmEmail}
            marginBottom
          />
        </Box>

        <FormSelect
          id='preferContact'
          label={'How would you prefer us to contact you?'}
          {...register('preferContact')}
          error={errors?.preferContact}
          aria-placeholder='Yes'
          marginBottom
        >
          <Options
            optionsData={[
              { label: 'Email', value: 'email' },
              { label: 'Call', value: 'call' },
            ]}
          />
        </FormSelect>

        <Button type='submit' width='lg'>
          Submit
        </Button>
      </form>
    </Flex>
  );
};
